import { ResourceType, useHasPermission } from '../shared/permissions';
import type { Action } from '../shared/permissions'; import type { Team } from '../types';

/**
 * Returns the total number of projects and release groups in the team passed in.
 * We refer to this as the number of "projects" in a few places in the app even though this includes release groups.
 *
 * @param {Team} team - The team to get the total project count for.
 * @returns {number} The total number of projects and release groups in the team.
 */
export const totalProjectCount = ({
  teamProjects,
  teamProjectsCount,
  teamReleaseGroups,
  teamReleaseGroupsCount,
}: Team) =>
  (teamProjects?.length ?? teamProjectsCount ?? 0) + (teamReleaseGroups?.length ?? teamReleaseGroupsCount ?? 0);

/**
 * Hook for checking if the user has permission to perform an action on a team.
 *
 * @param {Action} action - The action to check for.
 * @param {number} teamId - The ID of the team to check for.
 * @returns {boolean} Whether the user has permission for the team.
 */
export const useHasPermissionForTeam = (action: Action, teamId: number) => {
  const hasPermission = useHasPermission();
  return (
    hasPermission({ resourceType: ResourceType.TeamAny, action }) ||
    hasPermission({ resourceType: ResourceType.Team, action, teamId })
  );
};
